import React from 'react';

const GlassyBadge = ({
  children,
  icon: Icon,
  size = 'md',
  color = 'primary',
  className = '',
  variant = 'default', // 'default', 'outline', 'solid'
  level = null, // for N-level badges (0-5)
  srsStage = null, // for SRS stage badges (0-7)
  politeness = null, // for politeness badges ('casual', 'polite', 'humble', 'honorific')
  bounce = false // for bounce animation
}) => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm'
  };

  // Map color prop to CSS variable-based styles  
  const getRegularColorStyle = (colorName) => {
    const validColors = ['primary', 'orange', 'green', 'blue', 'purple', 'red', 'emerald', 'amber', 'violet', 'neutral', 'yellow'];
    const c = validColors.includes(colorName) ? colorName : 'primary';
    return {
      background: `linear-gradient(to bottom right, var(--color-glassy-${c}), var(--color-glassy-${c}-to))`,
      boxShadow: `0 4px 6px -1px color-mix(in srgb, var(--color-glassy-${c}) 40%, transparent)`,
    };
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const isGlassy = variant === 'default' || variant === 'outline';
  const isSolid = variant === 'solid';
  const isOutline = variant === 'outline';

  // Handle special cases for level and srsStage using CSS variables
  let customStyle = {};
  let hasCustomColor = false;
  
  // Define CSS variable-based styles for N-levels
  const getLevelColorStyle = (levelNum) => {
    const n = (levelNum >= 0 && levelNum <= 5) ? levelNum : 0;
    return {
      background: `linear-gradient(to bottom right, var(--color-n-${n}), var(--color-n-${n}))`,
      boxShadow: `0 4px 6px -1px color-mix(in srgb, var(--color-n-${n}) 50%, transparent)`,
    };
  };

  // Define CSS variable-based styles for SRS stages
  const getSrsColorStyle = (srsNum) => {
    const n = (srsNum >= 0 && srsNum <= 7) ? srsNum : 0;
    return {
      background: `linear-gradient(to bottom right, var(--color-srs-${n}), var(--color-srs-${n}))`,
      boxShadow: `0 4px 6px -1px color-mix(in srgb, var(--color-srs-${n}) 50%, transparent)`,
    };
  };

  // Define CSS variable-based styles for politeness levels
  const getPolitenessColorStyle = (politenessType) => {
    const validTypes = ['casual', 'polite', 'humble', 'honorific'];
    if (!validTypes.includes(politenessType)) return {};
    return {
      background: `linear-gradient(to bottom right, var(--color-politeness-${politenessType}), var(--color-politeness-${politenessType}))`,
      boxShadow: `0 4px 6px -1px color-mix(in srgb, var(--color-politeness-${politenessType}) 50%, transparent)`,
    };
  };
  
  if (level !== null) {
    customStyle = getLevelColorStyle(level);
    hasCustomColor = true;
  } else if (srsStage !== null) {
    customStyle = getSrsColorStyle(srsStage);
    hasCustomColor = true;
  } else if (politeness !== null) {
    customStyle = getPolitenessColorStyle(politeness);
    hasCustomColor = true;
  } else if (!isOutline) {
    customStyle = getRegularColorStyle(color);
    hasCustomColor = true;
  }

  const badgeClasses = `
    relative inline-flex items-center justify-center font-bold rounded-full
    ${sizeClasses[size]}
    ${isGlassy ? 'backdrop-blur-sm border border-white/20' : ''}
    ${isOutline ? 'bg-transparent border-2' : ''}
    ${isSolid && hasCustomColor ? 'shadow-none' : ''}
    ${bounce ? 'animate-bounce' : ''}
    ${className}
  `.replace(/\s+/g, ' ').trim();

  const textColor = isOutline 
    ? `text-${color}-500 border-${color}-500/50`
    : 'text-white drop-shadow-sm';

  return (
    <div 
      className={badgeClasses}
      style={hasCustomColor ? customStyle : {}}
    >
      {isGlassy && !isOutline && (
        <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-70 dark:from-white/10 dark:opacity-50 rounded-full" />
      )}
      
      <div className={`relative z-10 flex items-center ${textColor}`}>
        {Icon && (
          React.createElement(Icon, { className: `${iconSizes[size]} ${children ? 'mr-1' : ''}` })
        )}
        {children}
      </div>
    </div>
  );
};

export default GlassyBadge;